import { useState } from 'react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Sparkles } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
import { usePermissions } from '../hooks/usePermissions';
import type { ProjectHealth, ProjectStatus } from '../lib/projectsApi';
import {
  HealthBadge,
  PROJECT_HEALTHS,
  PROJECT_STATUSES,
  StatusBadge,
  healthLabel,
  statusLabel,
} from '../components/projects/ProjectBadges';
import { CreateProjectModal } from '../components/projects/CreateProjectModal';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { Select } from '../components/ui/Select';
import { useI18n } from '../contexts/I18nContext';

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ro-RO') : '—';
}

const PAGE_SIZE = 20;

export function ProjectsListPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { can } = usePermissions();
  const canCreateProjects = can('projects', 'canCreate');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [health, setHealth] = useState<ProjectHealth | ''>('');
  const [sort, setSort] = useState('-createdAt');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  // Search as the user types, without a request per keystroke.
  const debouncedSearch = useDebouncedValue(search.trim());
  const { data, isLoading, isError } = useProjects({
    search: debouncedSearch || undefined,
    status: status ? [status] : undefined,
    health: health ? [health] : undefined,
    sort,
    page,
    limit: PAGE_SIZE,
  });

  const projects = data?.projects ?? [];
  const meta = data?.meta;

  // Reset to page 1 whenever a filter changes.
  function onFilterChange<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  const hasFilters = Boolean(search || status || health);

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('projects.title')}</h1>
        {canCreateProjects && (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/projects/new"
              className="inline-flex items-center justify-center gap-1.5 rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Sparkles size={16} /> {t('projects.createWithAI')}
            </Link>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center gap-1.5 rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus size={16} /> {t('projects.create')}
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="w-full sm:w-64">
          <label className="ui-field-label">{t('common.search')}</label>
          <input
            type="search"
            value={search}
            onChange={(e) => onFilterChange(setSearch)(e.target.value)}
            placeholder={t('projects.searchPlaceholder')}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
        <Select
          label={t('common.status')}
          clearable
          value={status}
          onChange={(v) => onFilterChange(setStatus)(v as ProjectStatus | '')}
          placeholder={t('projects.allStatuses')}
          options={PROJECT_STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))}
          className="w-full sm:w-44"
        />
        <Select
          label={t('projects.col.health')}
          clearable
          value={health}
          onChange={(v) => onFilterChange(setHealth)(v as ProjectHealth | '')}
          placeholder={t('projects.allHealths')}
          options={PROJECT_HEALTHS.map((h) => ({ value: h, label: healthLabel(h) }))}
          className="w-full sm:w-44"
        />
        <Select
          label={t('projects.sort.label')}
          value={sort}
          onChange={setSort}
          options={[
            { value: '-createdAt', label: t('projects.sort.newest') },
            { value: 'createdAt', label: t('projects.sort.oldest') },
            { value: 'name', label: t('projects.sort.nameAsc') },
            { value: '-name', label: t('projects.sort.nameDesc') },
            { value: '-endDate', label: t('projects.sort.endDateDesc') },
            { value: 'endDate', label: t('projects.sort.endDateAsc') },
          ]}
          className="w-full sm:w-48"
        />
        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setStatus('');
              setHealth('');
              setPage(1);
            }}
            className="text-sm text-blue-600 underline"
          >
            {t('projects.clearFilters')}
          </button>
        )}
      </div>

      <div className="mt-6">
        {isLoading && <SkeletonTable rows={8} columns={6} />}
        {isError && <p className="text-red-600">{t('projects.error.list')}</p>}
        {!isLoading && !isError && projects.length === 0 && (
          <p className="text-gray-500">{t('projects.empty')}</p>
        )}
        {projects.length > 0 && (
          <FadeIn className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">{t('projects.col.name')}</th>
                  <th className="px-4 py-3">{t('projects.col.status')}</th>
                  <th className="px-4 py-3">{t('projects.col.health')}</th>
                  <th className="px-4 py-3">{t('projects.col.owner')}</th>
                  <th className="px-4 py-3">{t('projects.col.team')}</th>
                  <th className="px-4 py-3">{t('projects.col.deadline')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {projects.map((project) => (
                  <tr
                    key={project.id}
                    onClick={() => navigate(`/projects/${project.id}`)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{project.name}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={project.status} />
                    </td>
                    <td className="px-4 py-3">
                      <HealthBadge health={project.health} />
                    </td>
                    <td className="px-4 py-3 text-gray-700">{project.owner?.name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{project.memberCount}</td>
                    <td className="px-4 py-3 text-gray-700">{formatDate(project.endDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FadeIn>
        )}

        {meta && meta.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {t('projects.pageInfo', {
                page: meta.page,
                totalPages: meta.totalPages,
                total: meta.total,
              })}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={meta.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                {t('projects.prev')}
              </button>
              <button
                type="button"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                {t('projects.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateProjectModal open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}
